const { deepClone } = require('./deepClone');

/**
 * @typedef {'ADDED' | 'MODIFIED' | 'DELETED'} DiffType
 */

/**
 * @typedef {Object} DiffObject
 * @property {string} path
 * @property {DiffType} type
 * @property {unknown} [original]
 * @property {unknown} [update]
 */

/**
 * @typedef {Record<string, unknown> | unknown[]} Comparable
 */

/**
 * @typedef {Object} ComparationResult
 * @property {boolean} hasChanged
 * @property {DiffObject} updates
 */

/**
 * @typedef {Object} DiffContext
 * @property {string} path
 * @property {ComparationResult} result
 */

/**
 * Compares 2 objects and updates the context object with the differences
 * @param {Record<string, unknown>} original
 * @param {Record<string, unknown>} update
 * @param {DiffContext} context
 */
function compareObjects(original, update, context) {
  const { path, result } = context;

  if (!original && update) {
    return (result[path] = toDiffObject('ADDED', {
      path,
      update,
      original,
    }));
  }

  if (original && !update) {
    return (result[path] = toDiffObject('DELETED', {
      path,
      update,
      original,
    }));
  }

  const items = new Set(
    Object.keys(original || {}).concat(Object.keys(update || {})),
  );

  items.forEach((key) => {
    const fullPath = getObjectPath(key, original, update, path);

    if (isNestedObject(key, original) && isNestedObject(key, update)) {
      return compareObjects(original[key], update[key], {
        path: fullPath,
        result,
      });
    }

    if (isArray(original[key]) && isArray(update[key])) {
      const arr1 = original[key];
      const arr2 = update[key];

      return compareArrays(arr1, arr2, {
        path: fullPath,
        result,
      });
    }

    if (!isEqualProperty(key, original, update)) {
      const type = getChangeType(key, original, update);
      const updateValue = !(key in update) ? null : update[key];
      const originalValue = !(key in original) ? null : original[key];

      result[fullPath] = toDiffObject(type, {
        path: fullPath,
        original: originalValue,
        update: updateValue,
      });
    }
  });
}

/**
 * Compares 2 arrays and updates the context object with the differences
 * @param {unknown[]} original
 * @param {unknown[]} update
 * @param {DiffContext} context
 */
function compareArrays(original, update, context) {
  const { path, result } = context;
  const items = original.concat(update);

  if (!original && update) {
    return (result[path] = toDiffObject('ADDED', {
      path,
      update,
      original,
    }));
  }

  if (original && !update) {
    return (result[path] = toDiffObject('DELETED', {
      path,
      update,
      original,
    }));
  }

  items.forEach((_, idx) => {
    const item = original[idx];
    const newItem = update[idx];
    const fullPath = getObjectPath(idx, original, update, path);

    if (isArray(item) && isArray(newItem)) {
      return compareArrays(item, newItem, {
        path: fullPath,
        result,
      });
    }

    if (isObject(item) && isObject(newItem)) {
      return compareObjects(item, newItem, {
        path: fullPath,
        result,
      });
    }

    if (item !== newItem) {
      const type = getChangeType(idx, original, update);

      result[fullPath] = toDiffObject(type, {
        path: fullPath,
        original: item,
        update: newItem,
      });
    }
  });
}

/**
 * Gets a string representation for the path of a property that has changed
 * @param {string | number} key
 * @param {Record<string, unknown> | unknown[]} original
 * @param {Record<string, unknown> | unknown[]} update
 * @param {string} acc
 * @returns {string}
 */
function getObjectPath(key, original, update, acc) {
  if (!acc) return isArray(original) && isArray(update) ? `[${key}]` : `${key}`;
  if (isArray(original) && isArray(update)) return `${acc}[${key}]`;

  return `${acc}.${key}`;
}

/**
 * Determines whether the change is an new, modified or deleted value
 * and returns the proper action constant
 * @param {string | number} key
 * @param {Record<string, unknown> | unknown[]} original
 * @param {Record<string, unknown> | unknown[]} update
 * @returns {DiffType}
 */
function getChangeType(key, original, update) {
  if (isArray(original) && isArray(update)) {
    const item = original[key];
    const newItem = update[key];

    if (!valueExists(item) && valueExists(newItem)) return 'ADDED';

    if (valueExists(item) && !valueExists(newItem)) return 'DELETED';

    return 'MODIFIED';
  }

  const keyInOriginal = valueExists(original[key]) && key in original;
  const keyInUpdate = valueExists(update[key]) && key in update;

  if (keyInOriginal && !keyInUpdate) return 'DELETED';
  if (!keyInOriginal && keyInUpdate) return 'ADDED';

  return 'MODIFIED';
}

/**
 * Maps a raw diff object to the proper shape given a diff type
 * @param {DiffType} type
 * @param {DiffObject} result
 * @returns {DiffObject}
 */
function toDiffObject(type, result) {
  if (type === 'ADDED') {
    return {
      type,
      path: result.path,
      update: result.update,
    };
  }

  if (type === 'MODIFIED') {
    return {
      type,
      path: result.path,
      original: result.original,
      update: result.update,
    };
  }

  return {
    type,
    path: result.path,
    original: result.original,
  };
}

/**
 * Validates the arguments passed to the main function
 * @param {unknown} original
 * @param {unknown} update
 * @throws {TypeError}
 */
function throwOnInvalidArguments(original, update) {
  if (!isCollection(original)) {
    throw new TypeError(
      `Invalid type on argument list at position 0, expected ['object' | 'array'] but got ${typeof original}`,
    );
  } else if (!isCollection(update)) {
    throw new TypeError(
      `Invalid type on argument list at position 1, expected ['object' | 'array'] but got '${typeof update}'`,
    );
  } else if (
    (isArray(original) && isObject(update)) ||
    (isArray(update) && isObject(original))
  ) {
    const typeofOriginal = isArray(original) ? 'array' : typeof original;
    const typeOfUpdate = isArray(update) ? 'array' : typeof update;

    throw new TypeError(
      `Input argument types don't match, got ['${typeofOriginal}', '${typeOfUpdate}']`,
    );
  }
}

/**
 * Checks if a value is undefined
 * @param {unknown} val
 * @returns {boolean}
 */
function valueExists(val) {
  return typeof val !== 'undefined' && val !== null;
}

/**
 * Checks if a value is of type 'object'
 * @param {unknown} val
 * @returns {boolean}
 */
function isCollection(val) {
  if (!valueExists(val)) return false;
  return typeof val === 'object';
}

/**
 * Checks if a value is of type 'object' and is an Array
 * @param {unknown} val
 * @returns {boolean}
 */
function isArray(val) {
  if (!valueExists(val)) return false;
  return Array.isArray(val);
}

/**
 * Checks if a value is of type 'object' and is not Array
 * @param {unknown} val
 * @returns {boolean}
 */
function isObject(val) {
  if (!valueExists(val)) return false;
  return typeof val === 'object' && !Array.isArray(val);
}

/**
 * Checks if the key passed is contained by the object
 * @param {string} key
 * @param {Record<string, unknown>} val
 * @returns {boolean}
 */
function isNestedObject(key, val) {
  const exists = isObject(val) && key in val;

  if (!exists) return false;

  return isObject(val[key]);
}

/**
 * Checks if provided property values match
 * @param {string} key
 * @param {Record<string, unknown>} obj1
 * @param {Record<string, unknown>} obj2
 * @returns {boolean}
 */
function isEqualProperty(key, obj1, obj2) {
  if (key in obj1 && !(key in obj2)) return false;
  if (key in obj2 && !(key in obj1)) return false;

  return obj1[key] === obj2[key];
}

function byJsonString(a, b) {
  const strA = JSON.stringify(a);
  const strB = JSON.stringify(b);

  if (strA === strB) return 0;

  return strA < strB ? -1 : 1;
}

/**
 * Compares two objects or arrays and returns the differences.
 * @param {Comparable} obj1 - The original object or array.
 * @param {Comparable} obj2 - The updated object or array.
 * @returns {ComparationResult} - The result of the comparison.
 */
module.exports = (obj1, obj2) => {
  const original = deepClone(obj1);
  const update = deepClone(obj2);
  const result = {};
  const path = '';

  throwOnInvalidArguments(original, update);

  if (isArray(original) && isArray(update)) {
    compareArrays(original, update, { path, result });
  }

  if (isObject(original) && isObject(update)) {
    compareObjects(original, update, { path, result });
  }

  return { hasChanged: Boolean(Object.keys(result).length), updates: result };
};
